import { createTheme, responsiveFontSizes } from '@mui/material/styles';

/**
 * Design tokens — single source of truth shared with index.css.
 * Values here must match the CSS variables in :root and [data-theme="dark"].
 */
const lightTokens = {
  // Surfaces
  bgApp:      '#F7F8FB',
  bgSurface:  '#FFFFFF',
  bgSubtle:   '#F1F4F9',
  bgOverlay:  'rgba(15, 23, 42, 0.4)',
  // Ink
  textPrimary:   '#0B1220',
  textSecondary: '#5A6478',
  textTertiary:  '#94A0B5',
  textDisabled:  '#B8C0D0',
  onAccent:      '#FFFFFF',
  // Borders
  borderSubtle:  '#E6EAF2',
  borderDefault: '#DCE3EE',
  borderStrong:  '#C5CFE0',
  // Accent
  accent:           '#2563EB',
  accentHover:      '#1D4ED8',
  accentPressed:    '#1E40AF',
  accentSoft:       '#EFF4FF',
  accentSoftHover:  '#DCE7FE',
  accentGlow:       'rgba(37, 99, 235, 0.18)',
  accentFocusRing:  'rgba(37, 99, 235, 0.25)',
  // Status
  success:        '#10B981',
  successStrong:  '#059669',
  successSoft:    '#ECFDF5',
  warning:        '#D97706',
  warningStrong:  '#B45309',
  warningSoft:    '#FFFBEB',
  error:          '#DC2626',
  errorStrong:    '#B91C1C',
  errorSoft:      '#FEF2F2',
  info:           '#0EA5E9',
  infoStrong:     '#0284C7',
  infoSoft:       '#F0F9FF',
};

const darkTokens = {
  // Surfaces
  bgApp:      '#0B1220',
  bgSurface:  '#131B2E',
  bgSubtle:   '#1A2238',
  bgOverlay:  'rgba(2, 6, 23, 0.7)',
  // Ink
  textPrimary:   '#E5EAF2',
  textSecondary: '#9CA8BC',
  textTertiary:  '#6E7A91',
  textDisabled:  '#4A5468',
  onAccent:      '#FFFFFF',
  // Borders
  borderSubtle:  '#20294A',
  borderDefault: '#2A3656',
  borderStrong:  '#3B4670',
  // Accent — slightly lighter so the same blue reads as confident on dark
  accent:           '#3B82F6',
  accentHover:      '#60A5FA',
  accentPressed:    '#2563EB',
  accentSoft:       '#1E2A4A',
  accentSoftHover:  '#22335A',
  accentGlow:       'rgba(59, 130, 246, 0.28)',
  accentFocusRing:  'rgba(59, 130, 246, 0.32)',
  // Status — lifted fg with dark soft bgs
  success:        '#34D399',
  successStrong:  '#6EE7B7',
  successSoft:    '#0B2A22',
  warning:        '#FBBF24',
  warningStrong:  '#FCD34D',
  warningSoft:    '#2B1E0A',
  error:          '#F87171',
  errorStrong:    '#FCA5A5',
  errorSoft:      '#2A1212',
  info:           '#38BDF8',
  infoStrong:     '#7DD3FC',
  infoSoft:       '#0B2336',
};

const fs = {
  '2xs': '0.6875rem',
  xs:   '0.75rem',
  sm:   '0.8125rem',
  base: '0.875rem',
  md:   '0.9375rem',
  lg:   '1rem',
  xl:   '1.125rem',
  '2xl':'1.375rem',
  '3xl':'1.75rem',
  '4xl':'2.25rem',
  '5xl':'3rem',
};

const fw = { regular: 400, medium: 500, semibold: 600, bold: 700 };

const lightShadows = {
  xs:    '0 1px 2px rgba(15, 23, 42, 0.04)',
  sm:    '0 1px 3px rgba(15, 23, 42, 0.05), 0 1px 2px rgba(15, 23, 42, 0.03)',
  md:    '0 4px 12px rgba(15, 23, 42, 0.05), 0 2px 4px rgba(15, 23, 42, 0.03)',
  lg:    '0 12px 32px rgba(15, 23, 42, 0.08), 0 4px 8px rgba(15, 23, 42, 0.04)',
  xl:    '0 24px 64px rgba(15, 23, 42, 0.10), 0 8px 16px rgba(15, 23, 42, 0.05)',
  glow:  '0 8px 24px rgba(37, 99, 235, 0.18)',
};
const darkShadows = {
  xs:    '0 1px 2px rgba(0, 0, 0, 0.40)',
  sm:    '0 1px 3px rgba(0, 0, 0, 0.45), 0 1px 2px rgba(0, 0, 0, 0.30)',
  md:    '0 4px 12px rgba(0, 0, 0, 0.50), 0 2px 4px rgba(0, 0, 0, 0.35)',
  lg:    '0 12px 32px rgba(0, 0, 0, 0.55), 0 4px 8px rgba(0, 0, 0, 0.40)',
  xl:    '0 24px 64px rgba(0, 0, 0, 0.60), 0 8px 16px rgba(0, 0, 0, 0.45)',
  glow:  '0 8px 24px rgba(59, 130, 246, 0.28)',
};

export const designTokens = { light: lightTokens, dark: darkTokens, fs, fw, shadows: lightShadows };

// Wrap with responsiveFontSizes so h1-h6 scale down on small viewports.
// Body/button/caption keep their fixed sizes to preserve text density.
export const createMUITheme = (mode = 'light') => {
  const tokens = mode === 'dark' ? darkTokens : lightTokens;
  const shadows = mode === 'dark' ? darkShadows : lightShadows;
  return responsiveFontSizes(createTheme({
  palette: {
    mode,
    primary: {
      main: tokens.accent,
      light: tokens.accentSoft,
      dark: tokens.accentPressed,
      contrastText: tokens.onAccent,
    },
    secondary: {
      main: tokens.textPrimary,
      light: tokens.textSecondary,
      dark: '#000000',
      contrastText: tokens.onAccent,
    },
    success: { main: tokens.success, light: tokens.successSoft, dark: tokens.successStrong, contrastText: tokens.onAccent },
    warning: { main: tokens.warning, light: tokens.warningSoft, dark: tokens.warningStrong, contrastText: tokens.onAccent },
    error:   { main: tokens.error,   light: tokens.errorSoft,   dark: tokens.errorStrong,   contrastText: tokens.onAccent },
    info:    { main: tokens.info,    light: tokens.infoSoft,    dark: tokens.infoStrong,    contrastText: tokens.onAccent },
    background: {
      default: tokens.bgApp,
      paper:   tokens.bgSurface,
    },
    text: {
      primary:   tokens.textPrimary,
      secondary: tokens.textSecondary,
      disabled:  tokens.textDisabled,
    },
    divider: tokens.borderSubtle,
    action: {
      hover: tokens.bgSubtle,
      selected: tokens.accentSoft,
      disabled: tokens.textDisabled,
      disabledBackground: tokens.bgSubtle,
      focus: tokens.accentFocusRing,
    },
  },
  shape: { borderRadius: 12 },
  shadows: [
    'none',
    shadows.xs,
    shadows.sm,
    shadows.sm,
    shadows.md,
    shadows.md,
    shadows.md,
    shadows.md,
    shadows.lg,
    shadows.lg,
    shadows.lg,
    shadows.lg,
    shadows.lg,
    shadows.lg,
    shadows.lg,
    shadows.lg,
    shadows.xl,
    shadows.xl,
    shadows.xl,
    shadows.xl,
    shadows.xl,
    shadows.xl,
    shadows.xl,
    shadows.xl,
    shadows.xl,
  ],
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: 14,
    htmlFontSize: 16,
    fontWeightLight: fw.regular,
    fontWeightRegular: fw.regular,
    fontWeightMedium: fw.medium,
    fontWeightBold: fw.semibold,
    h1:       { fontSize: fs['4xl'], fontWeight: fw.semibold, lineHeight: 1.15, letterSpacing: '-0.02em' },
    h2:       { fontSize: fs['3xl'], fontWeight: fw.semibold, lineHeight: 1.2,  letterSpacing: '-0.02em' },
    h3:       { fontSize: fs['2xl'], fontWeight: fw.semibold, lineHeight: 1.25, letterSpacing: '-0.015em' },
    h4:       { fontSize: fs.xl,     fontWeight: fw.semibold, lineHeight: 1.3,  letterSpacing: '-0.01em' },
    h5:       { fontSize: fs.lg,     fontWeight: fw.semibold, lineHeight: 1.35 },
    h6:       { fontSize: fs.md,     fontWeight: fw.semibold, lineHeight: 1.4 },
    subtitle1:{ fontSize: fs.lg,     fontWeight: fw.medium,   lineHeight: 1.5 },
    subtitle2:{ fontSize: fs.base,   fontWeight: fw.medium,   lineHeight: 1.5 },
    body1:    { fontSize: fs.base,   fontWeight: fw.regular,  lineHeight: 1.5 },
    body2:    { fontSize: fs.sm,     fontWeight: fw.regular,  lineHeight: 1.5 },
    button:   { fontSize: fs.base,   fontWeight: fw.semibold, lineHeight: 1.4, textTransform: 'none', letterSpacing: 0 },
    caption:  { fontSize: fs.xs,     fontWeight: fw.regular,  lineHeight: 1.4 },
    overline: { fontSize: fs.xs,     fontWeight: fw.semibold, lineHeight: 1.4, letterSpacing: '0.08em', textTransform: 'uppercase' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tokens.bgApp,
          color: tokens.textPrimary,
        },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          background: tokens.borderDefault,
          borderRadius: 999,
          border: `2px solid ${tokens.bgApp}`,
        },
        '*::-webkit-scrollbar-thumb:hover': { background: tokens.borderStrong },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: false },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: fw.semibold,
          borderRadius: 10,
          paddingInline: 16,
          paddingBlock: 8,
          transition: 'background-color 200ms cubic-bezier(0.16,1,0.3,1), box-shadow 200ms, border-color 200ms',
        },
        sizeSmall: { paddingInline: 12, paddingBlock: 6, fontSize: fs.sm },
        sizeLarge: { paddingInline: 20, paddingBlock: 12, fontSize: fs.md },
        containedPrimary: {
          backgroundColor: tokens.accent,
          '&:hover': { backgroundColor: tokens.accentHover, boxShadow: shadows.glow },
          '&:active': { backgroundColor: tokens.accentPressed },
        },
        outlinedPrimary: {
          borderColor: tokens.borderDefault,
          color: tokens.textPrimary,
          '&:hover': {
            borderColor: tokens.accent,
            backgroundColor: tokens.accentSoft,
            color: tokens.accent,
          },
        },
        textPrimary: {
          color: tokens.textPrimary,
          '&:hover': { backgroundColor: tokens.bgSubtle },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          color: tokens.textSecondary,
          '&:hover': { backgroundColor: tokens.bgSubtle, color: tokens.textPrimary },
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.bgSurface,
          borderRadius: 10,
          fontSize: fs.base,
          '& fieldset': { borderColor: tokens.borderDefault },
          '&:hover fieldset': { borderColor: tokens.borderStrong },
          '&.Mui-focused fieldset': { borderColor: tokens.accent, borderWidth: 1 },
          '&.Mui-focused': { boxShadow: `0 0 0 4px ${tokens.accentFocusRing}` },
          '&.Mui-disabled': { backgroundColor: tokens.bgSubtle },
        },
        input: {
          padding: '10px 14px',
          // iOS Safari zooms the page on focus when input font-size < 16px.
          // Bump to 16px on touch-capable / small viewports to suppress the zoom
          // without changing the rendered size in desktop browsers.
          '@media (max-width: 600px)': {
            fontSize: 16,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: fs.sm, color: tokens.textSecondary },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { marginLeft: 2, marginTop: 6, fontSize: fs.xs },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: tokens.bgSurface,
        },
        outlined: {
          borderColor: tokens.borderSubtle,
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: tokens.bgSurface,
          border: `1px solid ${tokens.borderSubtle}`,
          borderRadius: 16,
          boxShadow: shadows.xs,
          transition: 'box-shadow 200ms cubic-bezier(0.16,1,0.3,1), border-color 200ms',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: 24, '&:last-child': { paddingBottom: 24 } },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: tokens.borderSubtle } },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.bgSubtle,
          '& .MuiTableCell-head': {
            color: tokens.textSecondary,
            fontWeight: fw.semibold,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontSize: fs.xs,
            borderBottom: `1px solid ${tokens.borderSubtle}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 150ms ease',
          '&:hover': { backgroundColor: tokens.bgSubtle },
          '&:last-child td': { borderBottom: 'none' },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: tokens.borderSubtle,
          color: tokens.textPrimary,
          fontSize: fs.base,
          paddingTop: 12,
          paddingBottom: 12,
        },
      },
    },
    MuiChip: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          fontWeight: fw.semibold,
          fontSize: fs.xs,
          height: 24,
          borderRadius: 999,
        },
        outlined: {
          borderColor: tokens.borderDefault,
          backgroundColor: tokens.bgSurface,
        },
        colorPrimary: {
          backgroundColor: tokens.accentSoft,
          color: tokens.accent,
          '&:hover': { backgroundColor: tokens.accentSoftHover },
        },
        colorSuccess: {
          backgroundColor: tokens.successSoft,
          color: tokens.successStrong,
        },
        colorWarning: {
          backgroundColor: tokens.warningSoft,
          color: tokens.warningStrong,
        },
        colorError: {
          backgroundColor: tokens.errorSoft,
          color: tokens.errorStrong,
        },
        colorInfo: {
          backgroundColor: tokens.infoSoft,
          color: tokens.infoStrong,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          border: `1px solid ${tokens.borderSubtle}`,
          boxShadow: shadows.xl,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontSize: fs.xl, fontWeight: fw.semibold, paddingTop: 20, paddingBottom: 12 },
      },
    },
    MuiSelect: {
      defaultProps: { size: 'small' },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          marginTop: 6,
          borderRadius: 12,
          border: `1px solid ${tokens.borderSubtle}`,
          boxShadow: shadows.lg,
        },
        list: { padding: 6 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: fs.base,
          paddingTop: 8,
          paddingBottom: 8,
          '&:hover': { backgroundColor: tokens.bgSubtle },
          '&.Mui-selected': { backgroundColor: tokens.accentSoft, color: tokens.accent },
          '&.Mui-selected:hover': { backgroundColor: tokens.accentSoftHover },
        },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: {
          backgroundColor: tokens.textPrimary,
          color: tokens.onAccent,
          fontSize: fs.xs,
          fontWeight: fw.medium,
          padding: '6px 10px',
          borderRadius: 6,
        },
        arrow: { color: tokens.textPrimary },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid',
          fontSize: fs.base,
          padding: '10px 14px',
        },
        standardSuccess: { backgroundColor: tokens.successSoft, color: tokens.successStrong, borderColor: '#A7F3D0' },
        standardWarning: { backgroundColor: tokens.warningSoft, color: tokens.warningStrong, borderColor: '#FDE68A' },
        standardError:   { backgroundColor: tokens.errorSoft,   color: tokens.errorStrong,   borderColor: '#FECACA' },
        standardInfo:    { backgroundColor: tokens.infoSoft,    color: tokens.infoStrong,    borderColor: '#BAE6FD' },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 999, height: 6, backgroundColor: tokens.bgSubtle },
        bar:  { borderRadius: 999, backgroundColor: tokens.accent },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: { padding: 6 },
        switchBase: {
          '&.Mui-checked + .MuiSwitch-track': { backgroundColor: tokens.accent, opacity: 1 },
        },
        track: { borderRadius: 999, backgroundColor: tokens.borderStrong, opacity: 1 },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: { color: tokens.borderStrong, '&.Mui-checked': { color: tokens.accent } },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: { color: tokens.borderStrong, '&.Mui-checked': { color: tokens.accent } },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: { backgroundColor: tokens.bgOverlay, backdropFilter: 'blur(4px)' },
      },
    },
  },
}));
};
